import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useToasts } from 'ui/toast';
import { useAllProjectTranscripts } from 'hooks/transcripts/queries';
import { useCodeExports } from 'hooks/exports/useCodeExports';
import { useExportMatrixActions } from 'hooks/exports/useExportData';
import { useSnippetExport } from 'hooks/exports/useSnippetExport';
import { useCodingsExport } from 'hooks/exports/useCodingsExport';
import { useDemographicsExport } from 'hooks/exports/useDemographicsExport';
import { useTranscriptsVDescriptorExport } from 'hooks/exports/useTranscriptsVDescriptorExport';
import { useExportTranscriptMutation } from 'hooks/exports/mutations';
import LogAPI from 'apis/LogAPI';
import useTrackingPage from 'hooks/useTrackingPage';
import { OP_DEFAULT } from 'constants/OpConstants';
import { ALL_REPORTS, DEFAULT_REPORT_ID } from './reports/reportSections';
import ToTranscriptContentWrapper from './ToTranscriptContentWrapper';
import ReportsSidebar from './reports/ReportsSidebar';
import ReportDetailPanel from './reports/ReportDetailPanel';
import codeXCodeImg from 'images/reports/reports_codexcode.svg';
import snippetsCsvImg from 'images/reports/reports_snippetsascsv.svg';

const getInitialFilters = () => ({
  codes: [],
  multi_choices: {},
  transcripts: [],
  surveys: [],
  op: OP_DEFAULT,
  sort: 'newest_activity'
});

const ReportsPage = (props) => {
  const params = useParams();
  const history = useHistory();
  const { addToast } = useToasts();
  const page = useTrackingPage();
  const projectID = props.projectID || params.projectID;
  const routeReportId = params.reportId;
  const onboardingState = props.onboardingState || {};

  const selected =
    ALL_REPORTS.find((r) => r.id === routeReportId) ||
    ALL_REPORTS.find((r) => r.id === DEFAULT_REPORT_ID) ||
    ALL_REPORTS[0];

  const setSelectedId = (id) => history.push(`/projects/${projectID}/reports/${id}`);

  const [filters, setFilters] = useState(getInitialFilters);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState(null);

  // Redirect to the default report when the URL's report id is missing or
  // invalid, so users never sit on /reports/<invalid> while a different report
  // renders (keeps deep links and refresh/share consistent).
  useEffect(() => {
    const isValidReport = ALL_REPORTS.some((r) => r.id === routeReportId);
    if (projectID && !isValidReport) {
      history.replace(`/projects/${projectID}/reports/${DEFAULT_REPORT_ID}`);
    }
  }, [routeReportId, projectID, history]);

  // Transcript list for transcript selector
  const { data: transcriptsData } = useAllProjectTranscripts(projectID, { enabled: !!projectID });
  const transcripts = useMemo(
    () => (transcriptsData || [])
      .map((t) => ({ id: t.id, name: t.name && t.name.trim() !== '' ? t.name : 'Untitled' }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [transcriptsData]
  );

  // Export hooks
  const { exportCsv, exportWord, isExportingCsv, isExportingWord, isReady: codesReady } = useCodeExports(projectID);

  const {
    isReady: matrixReady,
    exportCodesVCodes,
    exportCodesVTranscripts,
    exportCodesVDescriptor,
    isExportingCodesVCodes,
    isExportingCodesVTranscripts,
    isExportingCodesVDescriptor,
  } = useExportMatrixActions(projectID);

  const { exportSnippets, isExporting: isExportingSnippets, isReady: snippetsReady } = useSnippetExport(projectID, filters);

  const { exportCodings, isExporting: isExportingCodings, isReady: codingsReady } = useCodingsExport(projectID, filters);

  const { exportDemographics, isExporting: isExportingDemographics, isReady: demographicsReady } = useDemographicsExport(projectID);

  const {
    exportTranscriptsVDescriptor,
    isExporting: isExportingTranscriptsVDescriptor,
    isReady: transcriptsVDescriptorReady
  } = useTranscriptsVDescriptorExport(projectID);

  const exportTranscriptMutation = useExportTranscriptMutation();
  const [exportingTranscriptId, setExportingTranscriptId] = useState(null);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const isTranscriptDropdownReport = selected.kind === 'transcript-select' || selected.kind === 'coded-transcript-select';

  const handleExport = useCallback(() => {
    switch (selected.id) {
      case 'transcripts':
        if (!selectedTranscriptId) {
          addToast('Please select a transcript.', { appearance: 'warning', autoDismiss: true });
          return;
        }
        setExportingTranscriptId(selectedTranscriptId);
        exportTranscriptMutation.mutate(selectedTranscriptId, {
          onSettled: () => setExportingTranscriptId(null),
        });
        break;
      case 'coded-transcript':
        if (!selectedTranscriptId) {
          addToast('Please select a transcript.', { appearance: 'warning', autoDismiss: true });
          return;
        }
        // Logged at the open (the print view is a separate window/SPA route that
        // renders client-side), so there is no Rails controller to log it on.
        LogAPI.logEvent('export-coded-transcript', { page });
        window.open(`/transcripts/${selectedTranscriptId}/print`, '_blank');
        break;
      case 'codes-doc':
        codesReady && exportWord();
        break;
      case 'codes-csv':
        codesReady && exportCsv();
        break;
      case 'snippets-csv':
        snippetsReady && exportSnippets();
        break;
      case 'codings-csv':
        codingsReady && exportCodings();
        break;
      case 'demographics-csv':
        demographicsReady && exportDemographics();
        break;
      case 'transcript-x-descriptor':
        transcriptsVDescriptorReady && exportTranscriptsVDescriptor();
        break;
      case 'code-x-code':
        matrixReady && exportCodesVCodes();
        break;
      case 'code-x-transcript':
        matrixReady && exportCodesVTranscripts();
        break;
      case 'code-x-descriptor':
        matrixReady && exportCodesVDescriptor();
        break;
    }
  }, [selected.id, selectedTranscriptId, codesReady, snippetsReady, codingsReady, demographicsReady, transcriptsVDescriptorReady, matrixReady,
      addToast, page, exportTranscriptMutation, exportWord, exportCsv, exportSnippets, exportCodings, exportDemographics,
      exportTranscriptsVDescriptor, exportCodesVCodes, exportCodesVTranscripts, exportCodesVDescriptor]);

  const exporting = useMemo(() => {
    switch (selected.id) {
      case 'transcripts': return exportingTranscriptId !== null;
      case 'codes-doc': return isExportingWord;
      case 'codes-csv': return isExportingCsv;
      case 'snippets-csv': return isExportingSnippets;
      case 'codings-csv': return isExportingCodings;
      case 'demographics-csv': return isExportingDemographics;
      case 'transcript-x-descriptor': return isExportingTranscriptsVDescriptor;
      case 'code-x-code': return isExportingCodesVCodes;
      case 'code-x-transcript': return isExportingCodesVTranscripts;
      case 'code-x-descriptor': return isExportingCodesVDescriptor;
      default: return false;
    }
  }, [selected.id, exportingTranscriptId, isExportingWord, isExportingCsv, isExportingSnippets,
      isExportingCodings, isExportingDemographics, isExportingTranscriptsVDescriptor, isExportingCodesVCodes,
      isExportingCodesVTranscripts, isExportingCodesVDescriptor]);

  const disabled = useMemo(() => {
    if (exporting) return true;
    if (isTranscriptDropdownReport && !selectedTranscriptId) return true;
    switch (selected.id) {
      case 'codes-doc':
      case 'codes-csv':
        return !codesReady;
      case 'snippets-csv':
        return !snippetsReady;
      case 'codings-csv':
        return !codingsReady;
      case 'demographics-csv':
        return !demographicsReady;
      case 'transcript-x-descriptor':
        return !transcriptsVDescriptorReady;
      case 'code-x-code':
      case 'code-x-transcript':
      case 'code-x-descriptor':
        return !matrixReady;
      default:
        return false;
    }
  }, [selected.id, exporting, isTranscriptDropdownReport, selectedTranscriptId,
      codesReady, snippetsReady, codingsReady, demographicsReady, transcriptsVDescriptorReady, matrixReady]);

  return (
    <ToTranscriptContentWrapper
      loaded={onboardingState.loaded}
      showOnboarding={onboardingState.hasNoCodeableData}
      header="Export codes, snippets, and matrices into formats your whole team can use."
      body="Share PDFs with stakeholders, drop CSVs into other analysis tools, or keep a Word doc as your offline record."
      mediaType="image"
      mediaSrc={codeXCodeImg}
      secondMediaSrc={snippetsCsvImg}
      hasEditPermission={props.hasEditPermission}
      projectID={projectID}
    >
      <div style={{ display: 'flex', height: '100%', width: '100%', background: '#fff', overflowX: 'auto' }}>
        <ReportsSidebar selectedId={selected.id} onSelect={setSelectedId} />
        <ReportDetailPanel
          selected={selected}
          projectID={projectID}
          transcripts={transcripts}
          selectedTranscriptId={selectedTranscriptId}
          onTranscriptChange={setSelectedTranscriptId}
          filters={filters}
          onFilterChange={handleFilterChange}
          handleExport={handleExport}
          exporting={exporting}
          disabled={disabled}
        />
      </div>
    </ToTranscriptContentWrapper>
  );
};

export default ReportsPage;
